import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { useCallback } from "react";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAuth } from "../hooks/useAuth";
import BlankLayout from "../layouts/BlankLayout";
import Loader from "../components/common/status/Loader/Loader";
import type { RootStackParamList } from "../navigation/navigation.types";
import { UserRoleUI } from "@axio/auth-guard";
import Sentry from "../utils/sentry";

function LoginLTIILEView() {
  const route = useRoute<RouteProp<RootStackParamList, "LoginLTIILE">>();
  const { login } = useAuth();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      const triggerLogin = async () => {
        const { accessToken, refreshToken, activityId, deepLinkStateToken, integrationId, provider } =
          route.params ?? {};

        if (!accessToken || !refreshToken) {
          if (!isActive) return;
          navigation.reset({
            index: 0,
            routes: [
              {
                name: "LTIErrorView",
                params: {
                  error: "invalid_launch",
                },
              },
            ],
          });
          return;
        }

        let authResult;
        try {
          authResult = await login(accessToken, refreshToken);
        } catch (error) {
          Sentry.captureException(error);
          if (!isActive) return;
          navigation.reset({
            index: 0,
            routes: [
              {
                name: "LTIErrorView",
                params: {
                  error: "auth_failed",
                },
              },
            ],
          });
          return;
        }

        if (!isActive) return;

        const { userRole } = authResult;

        // Deep link takes highest priority — route to activity selection for faculty
        if (deepLinkStateToken) {
          navigation.reset({
            index: 0,
            routes: [
              {
                name: "LTIDeepLink",
                params: {
                  deepLinkStateToken,
                  integrationId,
                  provider,
                },
              },
            ],
          });
          return;
        }

        if (activityId) {
          if (userRole === UserRoleUI.STUDENT) {
            navigation.reset({
              index: 0,
              routes: [
                {
                  name: "LTIILE",
                  params: { activityId },
                },
              ],
            });
          } else {
            // instructor / admin → LTI activity analytics
            navigation.reset({
              index: 0,
              routes: [
                {
                  name: "LTI Analytics",
                  params: { activityId },
                },
              ],
            });
          }
          return;
        }

        // Missing actionable params
        navigation.reset({
          index: 0,
          routes: [
            {
              name: "LTIErrorView",
              params: {
                error: "missing_activity",
              },
            },
          ],
        });
      };

      triggerLogin();

      return () => {
        isActive = false;
      };
    }, [
      route.params?.accessToken,
      route.params?.refreshToken,
      route.params?.activityId,
      route.params?.deepLinkStateToken,
      route.params?.integrationId,
      route.params?.provider,
      login,
      navigation,
    ])
  );

  return (
    <BlankLayout>
      <Loader />
    </BlankLayout>
  );
}

export default LoginLTIILEView;
